import {
  FiCalendar,
  FiClock,
  FiDollarSign,
  FiDroplet,
  FiMapPin,
  FiScissors,
  FiUsers,
} from "react-icons/fi";
import { formatDate, formatTime } from "../../utils/formatDate";
import formatCurrency from "../../utils/formatCurrency";
import {
  JAGUAR_HENNA_DISCLAIMER,
  HENNA_TYPES,
  MOBILE_MODE_DESCRIPTION,
  MOBILE_TRAVEL_EXPENSES_DISCLAIMER,
  SERVICE_MODE,
  STUDIO_MODE_DESCRIPTION,
} from "../../utils/constants";

const DetailRow = ({ icon, label, value }) => {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 text-henna">{icon}</div>
      <div>
        <p className="text-sm font-medium text-[#3b2f2f]">{label}</p>
        <p className="mt-1 text-sm leading-6 text-muted-warm">{value || "—"}</p>
      </div>
    </div>
  );
};

const BookingSummary = ({
  service = null,
  bookingData = {},
  calculatedPartySize = 0,
  calculatedBookingPrice = 0,
  calculatedBookingDurationMinutes = 0,
  className = "",
  title = "Appointment Summary",
  showDescriptions = true,
}) => {
  const {
    bookingDate,
    startTime,
    endTime,
    serviceMode,
    locationNotes,
    onsiteAddress,
    notes,
    hennaType,
  } = bookingData || {};

  const addressText =
    onsiteAddress && serviceMode === SERVICE_MODE.MOBILE
      ? [
          onsiteAddress.line1,
          onsiteAddress.line2,
          onsiteAddress.city,
          onsiteAddress.state,
          onsiteAddress.zip,
        ]
          .filter(Boolean)
          .join(", ")
      : "";

  const modeDescription =
    serviceMode === SERVICE_MODE.MOBILE
      ? MOBILE_MODE_DESCRIPTION
      : serviceMode === SERVICE_MODE.STUDIO
      ? STUDIO_MODE_DESCRIPTION
      : "";

  const isJaguarSelected = hennaType === HENNA_TYPES.JAGUAR;

  return (
    <aside className={`content-card-lg p-6 ${className}`}>
      <h2 className="text-xl font-semibold text-henna">{title}</h2>
      <p className="mt-2 text-sm leading-6 text-muted-warm">
        Review your service, timing, and appointment details before you submit.
      </p>

      <div className="mt-6 space-y-5">
        <DetailRow
          icon={<FiScissors size={18} />}
          label="Service"
          value={service?.serviceName || "Select a service"}
        />

        <DetailRow
          icon={<FiDroplet size={18} />}
          label="Henna Type"
          value={hennaType || "Select a henna type"}
        />

        <DetailRow
          icon={<FiDollarSign size={18} />}
          label="Estimated Total Price"
          value={service ? formatCurrency(calculatedBookingPrice) : "To be determined"}
        />

        <DetailRow
          icon={<FiClock size={18} />}
          label="Estimated Duration"
          value={
            service
              ? `${calculatedBookingDurationMinutes} minutes`
              : "Not selected"
          }
        />

        <DetailRow
          icon={<FiCalendar size={18} />}
          label="Date"
          value={bookingDate ? formatDate(bookingDate) : "Not selected"}
        />

        <DetailRow
          icon={<FiClock size={18} />}
          label="Time"
          value={
            startTime && endTime
              ? `${formatTime(startTime)} - ${formatTime(endTime)}`
              : "Not selected"
          }
        />

        <DetailRow
          icon={<FiUsers size={18} />}
          label="Party Size"
          value={String(calculatedPartySize || 0)}
        />

        <DetailRow
          icon={<FiMapPin size={18} />}
          label="Appointment Type"
          value={serviceMode || "Not selected"}
        />

        {showDescriptions && modeDescription ? (
          <div className="rounded-2xl bg-soft-cream p-4">
            <p className="text-sm font-semibold text-henna">What This Means</p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {modeDescription}
            </p>
          </div>
        ) : null}

        {serviceMode === SERVICE_MODE.MOBILE ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-henna">Mobile Appointment Notice</p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {MOBILE_TRAVEL_EXPENSES_DISCLAIMER}
            </p>
          </div>
        ) : null}

        {isJaguarSelected ? (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
            <p className="text-sm font-semibold text-henna">Jaguar Henna Notice</p>
            <p className="mt-2 text-sm leading-6 text-muted-warm">
              {JAGUAR_HENNA_DISCLAIMER}
            </p>
          </div>
        ) : null}

        {serviceMode === SERVICE_MODE.MOBILE && addressText ? (
          <DetailRow
            icon={<FiMapPin size={18} />}
            label="Service Address"
            value={addressText}
          />
        ) : null}

        {serviceMode === SERVICE_MODE.MOBILE && locationNotes ? (
          <DetailRow
            icon={<FiMapPin size={18} />}
            label="Location Notes"
            value={locationNotes}
          />
        ) : null}

        {notes ? <DetailRow icon={<FiScissors size={18} />} label="Notes" value={notes} /> : null}
      </div>
    </aside>
  );
};

export default BookingSummary;
